import React, { FC } from "react";
import classNames from "classnames";
import { Fade } from "react-awesome-reveal";
import { StaticImage } from "gatsby-plugin-image";
import { useTranslation } from "react-i18next";
import { ApplePayLogo } from "@app/components/common/icons";
import { useGenericData } from "@app/hooks/use-generic-data/useGenericData";
import { DEFAULT_FADE_DURATION } from "@app/constants";

export const Hero: FC = () => {
  const { t } = useTranslation("p_bank_apple-pay");
  const { notificationBar } = useGenericData();

  return (
    <Fade duration={DEFAULT_FADE_DURATION} triggerOnce>
      <div
        className={classNames(
          "relative flex flex-col gap-y-10 px-5 pt-40",
          "lg:px-16 lg:pt-60",
          "xl:w-full xl:pb-0 xl:pt-0",
          "xl:gap-y-0 xl:px-24",
          "xl:items-center xl:justify-center",
          {
            "xl:mt-9.5 xl:h-[calc(100vh-38px)]": notificationBar?.isEnabled,
            "xl:h-screen": !notificationBar?.isEnabled,
          },
        )}
      >
        <div
          className={classNames(
            "hidden overflow-hidden",
            "xl:absolute xl:bottom-0 xl:left-0 xl:right-0 xl:top-0 xl:block",
          )}
        >
          <StaticImage
            src="./hero.webp"
            alt={"Apple Pay page"}
            className={classNames(
              "rounded-sm blur-lg",
              "xl:h-full xl:w-full xl:rounded-none",
            )}
          />
        </div>
        <div
          className={classNames(
            "bottom-0 left-0 right-0 top-0 hidden",
            "xl:absolute xl:block xl:bg-base-200 xl:opacity-50",
          )}
        />

        <div
          className={classNames(
            "relative order-first flex w-full flex-col gap-10",
            "text-primary-content",
            "lg:max-w-3/4",
            "xl:h-full xl:max-w-container-wide xl:flex-row xl:text-center",
            "xl:order-last xl:items-center xl:gap-15",
          )}
        >
          <div
            className={classNames("lg:w-3/4", "xl:h-fit xl:w-auto xl:flex-1")}
          >
            <ApplePayLogo
              className={classNames("mb-5 w-[87px]", "xl:mx-auto")}
            />
            <h1
              className={classNames(
                "mb-5 text-large",
                "lg:text-xx-large",
                "xl:mb-4 xl:text-huge",
              )}
            >
              {t("hero_title")}
            </h1>

            <p
              className={classNames(
                "text-regular text-base-400",
                "xl:text-primary-content",
              )}
            >
              {t("hero_body")}
            </p>
          </div>
          <div
            className={classNames(
              "relative aspect-square w-full overflow-hidden",
              "md:aspect-auto",
              "xl:flex xl:h-full xl:w-auto xl:flex-1 xl:flex-row xl:items-center xl:self-end",
            )}
          >
            <StaticImage
              src="./iphone.webp"
              alt={"Iphone"}
              className={classNames(
                "absolute left-1/2 top-0 w-4/5 -translate-x-1/2",
                "md:relative md:w-2/5 md:translate-y-0",
                "lg:w-1/3",
                "xl:w-2/3 xl:translate-y-[30%]",
              )}
            />
            <div
              className={classNames(
                "absolute -bottom-0 left-0 right-0 top-0 translate-y-1 bg-gradient-to-b from-transparent via-transparent to-base-200",
                "md:hidden",
              )}
            />
          </div>
        </div>
      </div>
    </Fade>
  );
};
